// store/useAppStore.ts — Global application state with persistence

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Theme, ResolvedTheme } from '@/types';

interface AppState {
  // ── Auth ──
  userId:        string | null;
  displayName:   string | null;
  token:         string | null;
  refreshToken:  string | null;
  isAuthenticated: boolean;
  // ── Tree ──
  activeTreeId: string | null;
  // ── Theme ──
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  // ── Locale ──
  locale: string;

  // ── Actions ──
  setAuthFull: (params: { userId: string; displayName: string; token: string; refreshToken: string; activeTreeId?: string | null }) => void;
  setAuth: (userId: string) => void;  // legacy — kept for compat
  clearAuth: () => void;
  setActiveTree: (treeId: string | null) => void;
  setTheme: (theme: Theme) => void;
  setResolvedTheme: (resolved: ResolvedTheme) => void;
  setLocale: (locale: string) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Initial state
      userId:        null,
      displayName:   null,
      token:         null,
      refreshToken:  null,
      isAuthenticated: false,
      activeTreeId: null,
      theme:         'night',   /* Vert & Or — Night mode default */
      resolvedTheme: 'night',
      locale:        'en',

      // Actions
      setAuthFull: ({ userId, displayName, token, refreshToken, activeTreeId }) =>
        set({
          userId,
          displayName,
          token,
          refreshToken,
          activeTreeId: (activeTreeId && activeTreeId !== 'sharma-family') ? activeTreeId : null,
          isAuthenticated: true,
        }),

      setAuth: (userId) =>
        set({ userId, isAuthenticated: true }),

      clearAuth: () =>
        set({
          userId: null,
          displayName: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          activeTreeId: null,
        }),

      setActiveTree: (treeId) =>
        set({ activeTreeId: (treeId && treeId !== 'sharma-family') ? treeId : null }),

      setTheme: (theme) =>
        set({ theme }),

      setResolvedTheme: (resolved) =>
        set({ resolvedTheme: resolved }),

      setLocale: (locale) =>
        set({ locale }),
    }),
    {
      name: 'foi-auth',
      version: 3,  // Bumped: forces migration to run for ALL stored sessions

      // Custom storage: sanitize sharma-family on EVERY read so stale localStorage
      // can never serve it even if the migration was previously skipped.
      storage: {
        getItem: (key) => {
          const raw = localStorage.getItem(key);
          if (!raw) return null;
          try {
            const parsed = JSON.parse(raw);
            if (parsed?.state?.activeTreeId === 'sharma-family') {
              parsed.state.activeTreeId = null;
            }
            return parsed;
          } catch {
            return null;
          }
        },
        setItem: (key, value) => {
          // Also sanitize on write for belt-and-suspenders
          try {
            const v = value as { state?: { activeTreeId?: string | null } };
            if (v?.state?.activeTreeId === 'sharma-family') {
              v.state.activeTreeId = null;
            }
          } catch { /* ignore */ }
          localStorage.setItem(key, JSON.stringify(value));
        },
        removeItem: (key) => localStorage.removeItem(key),
      },

      // Migration also runs for any stored state with version < 3
      migrate: (persisted: unknown) => {
        const s = persisted as Record<string, unknown>;
        return {
          ...s,
          activeTreeId: (s['activeTreeId'] && s['activeTreeId'] !== 'sharma-family')
            ? s['activeTreeId']
            : null,
        };
      },

      // Only persist what's needed; never write sharma-family
      partialize: (state) => ({
        userId:          state.userId,
        displayName:     state.displayName,
        token:           state.token,
        refreshToken:    state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        activeTreeId:    (state.activeTreeId && state.activeTreeId !== 'sharma-family') ? state.activeTreeId : null,
        theme:           state.theme,
        locale:          state.locale,
      }),
    }
  )
);

